import { Box, Divider, ScrollArea, Stack, Text } from "@mantine/core";

/**
 * Opaque data shape for the ChartEditor's optional right-rail Info
 * drawer. Each callsite (form image lightbox, Documents tab markup,
 * Medical Charts) constructs its own model, so the editor stays
 * unaware of where the bytes came from.
 */
export interface ImageInfoModel {
  fileName: string;
  description?: string | null;
  uploadedBy?: string | null;
  uploadDate?: string | null;
  fileType?: string | null;
  /** Client-side measured from the loaded <img> naturalWidth/Height. */
  dimensions?: { width: number; height: number } | null;
  /** Human-formatted size (e.g. "31.31 KB") — caller pre-formats. */
  formattedSize?: string | null;
}

interface ImageInfoPanelProps {
  model: ImageInfoModel;
  className?: string;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={500}>
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Stack>
  );
}

/**
 * Right-rail Info drawer matching the screenshot at
 * ~/pagoda/image_context/image_form/info_pannel.png. Rendered by
 * FormImageViewer in its own side panel.
 */
export function ImageInfoPanel({ model, className }: ImageInfoPanelProps) {
  const dims = model.dimensions
    ? `${model.dimensions.width} × ${model.dimensions.height}`
    : "—";
  return (
    <Box className={className}>
      <Box px="md" py="sm">
        <Text fw={700} size="md">
          Image details
        </Text>
        <Text size="xs" c="dimmed">
          Metadata for the current image.
        </Text>
        <Divider mt="xs" />
      </Box>
      <ScrollArea type="auto" px="md" pb="md">
        <Stack gap="md">
          <InfoRow label="File Name" value={model.fileName || "—"} />
          <InfoRow
            label="Description"
            value={
              model.description && model.description.trim().length > 0
                ? model.description
                : "—"
            }
          />
          <InfoRow label="Uploaded By" value={model.uploadedBy || "—"} />
          <InfoRow label="Upload Date" value={model.uploadDate || "—"} />
          <InfoRow label="File Type" value={model.fileType || "—"} />
          <InfoRow label="Dimensions" value={dims} />
          <InfoRow label="Size" value={model.formattedSize || "—"} />
        </Stack>
      </ScrollArea>
    </Box>
  );
}
